import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Icon, cn } from '@mypoz/ui';

import { AccountMenuButton, AccountSectionNav } from './AccountMenu.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { useFormat } from '@/lib/format.js';
import { isAccountPage, NAV, type NavGroupItem } from './navigation.js';
import { useNotifications } from '@/features/notifications/index.js';
import { SessionGate, useAccountSession } from '@/features/account/index.js';
import { useWallet } from '@/features/wallet/index.js';
import { TourProvider, useTours } from '@/features/tour/index.js';

const DESKTOP_BREAKPOINT = '(min-width: 1024px)';

const ENTRY_CLASS =
  'group relative flex min-h-12 items-center gap-3 rounded-md border-l-2 px-3 py-2.5 text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';
const ENTRY_ACTIVE = 'border-brand bg-brand-soft text-brand-dark';
const ENTRY_IDLE = 'border-transparent text-muted hover:bg-surface-alt hover:text-ink';

/**
 * The MyPoz logo (MYP-132) for the brand-red header and phone menu: "my" in the logo's own grey,
 * and "poz" and the bird in white, since the logo's red half would disappear on red. The tagline
 * is left off — at header height it would be a few pixels tall. The original artwork is in
 * `public/brand/`.
 */
function BrandLogo({ className }: { className?: string }) {
  const { t } = useTranslation();

  return (
    <img
      src="/brand/mypoz-wordmark-on-red.png"
      alt={t('app.name')}
      width={257}
      height={80}
      className={cn('w-auto', className)}
    />
  );
}

/** Renders nothing once everything is read, rather than showing a zero. */
function UnreadBadge() {
  const fmt = useFormat();
  const { unreadCount } = useNotifications();

  if (unreadCount === 0) return null;

  return (
    <span className="flex size-5 items-center justify-center rounded-full bg-brand text-[11px] font-semibold text-white">
      {fmt.unreadBadge(unreadCount)}
    </span>
  );
}

/**
 * A nav entry that owns sub-pages: the parent is a disclosure button rather than a link, because
 * it has no page of its own — clicking it opens the group and the children are the destinations.
 * Opens automatically when one of its children is the current route, so arriving at a sub-page
 * from elsewhere (the header bell, a Dashboard row) still shows where you are in the menu.
 */
function NavGroup({ item, onNavigate }: { item: NavGroupItem; onNavigate?: () => void }) {
  const { t: tNav } = useTranslation('nav');
  const { pathname } = useLocation();
  const holdsCurrentPage = item.children.some((child) => child.to === pathname);
  const [open, setOpen] = useState(holdsCurrentPage);
  const panelId = `nav-group-${item.key}`;

  useEffect(() => {
    if (holdsCurrentPage) setOpen(true);
  }, [holdsCurrentPage]);

  return (
    <li>
      <button
        type="button"
        data-tour={`nav-${item.key}`}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(ENTRY_CLASS, 'w-full', holdsCurrentPage ? ENTRY_ACTIVE : ENTRY_IDLE)}
      >
        <Icon name={item.icon} size={21} />
        <span className="min-w-0 flex-1 text-left">{tNav(item.key)}</span>
        {'unreadBadge' in item ? <UnreadBadge /> : null}
        <Icon
          name="expand_more"
          size={19}
          className={cn('text-muted-soft transition-transform', open && 'rotate-180')}
        />
      </button>

      {open ? (
        <ul id={panelId} className="mt-1 grid gap-1 pl-8">
          {item.children.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                onClick={onNavigate}
                className={({ isActive }) => cn(ENTRY_CLASS, isActive ? ENTRY_ACTIVE : ENTRY_IDLE)}
              >
                <span className="min-w-0 flex-1">{tNav(child.key)}</span>
                {'unreadBadge' in child ? <UnreadBadge /> : null}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function NavigationLinks({ onNavigate }: { onNavigate?: () => void }) {
  const { t: tNav } = useTranslation('nav');

  return (
    <nav aria-label={tNav('menu')} className="py-3">
      <ul className="grid gap-1 px-3">
        {NAV.map((item) =>
          'children' in item ? (
            <NavGroup key={item.to} item={item} onNavigate={onNavigate} />
          ) : (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/dashboard'}
                // Tours can point at any sidebar entry by key (MYP-83).
                data-tour={`nav-${item.key}`}
                onClick={onNavigate}
                className={({ isActive }) => cn(ENTRY_CLASS, isActive ? ENTRY_ACTIVE : ENTRY_IDLE)}
              >
                <Icon name={item.icon} size={21} />
                <span className="min-w-0 flex-1">{tNav(item.key)}</span>
              </NavLink>
            </li>
          ),
        )}
      </ul>
    </nav>
  );
}

/**
 * Replays the welcome tour (MYP-83), at the foot of the sidebar and of the phone menu. `before`
 * closes the menu first; the tour then opens a frame later, once focus is back on the ☰ button,
 * so that is where focus returns when the tour ends.
 */
function AppTourButton({ before }: { before?: () => void }) {
  const { t } = useTranslation();
  const { start } = useTours();

  return (
    <button
      type="button"
      onClick={() => {
        if (!before) {
          start('welcome');
          return;
        }
        before();
        window.requestAnimationFrame(() => start('welcome'));
      }}
      className={cn(ENTRY_CLASS, ENTRY_IDLE, 'w-full')}
    >
      <Icon name="tour" size={21} />
      <span className="min-w-0 flex-1 text-left">{t('tour.appTour')}</span>
    </button>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { t: tNav } = useTranslation('nav');
  const fmt = useFormat();
  const wallet = useWallet();
  const { profile } = useAccountSession();
  // Until the account has loaded there is no customer ID and no balance to show, and none is made
  // up: the header shows a dash for both, and `SessionGate` holds the page back.
  const customerId = profile?.customerId ?? '—';
  const balanceText = profile ? fmt.money(wallet.balanceCents, wallet.currency) : '—';
  const { unreadCount } = useNotifications();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    window.requestAnimationFrame(() => menuButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!drawerOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.querySelector<HTMLElement>('[data-drawer-close]')?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        drawerRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((element) => !element.hasAttribute('hidden'));

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeDrawer, drawerOpen]);

  useEffect(() => {
    const desktopQuery = window.matchMedia(DESKTOP_BREAKPOINT);

    const closeOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setDrawerOpen(false);
    };

    desktopQuery.addEventListener('change', closeOnDesktop);
    return () => desktopQuery.removeEventListener('change', closeOnDesktop);
  }, []);

  return (
    <TourProvider
      customerId={profile?.customerId}
      {...(profile?.name ? { name: profile.name } : {})}
    >
      <div className="min-h-dvh bg-canvas">
        <header className="sticky top-0 z-40 h-16 border-b border-black/10 bg-brand text-white shadow-sm">
          <div className="flex h-full items-center gap-2 px-3 sm:px-4 lg:gap-3 lg:px-6">
            <button
              ref={menuButtonRef}
              type="button"
              data-tour="menu-button"
              onClick={() => setDrawerOpen(true)}
              className="flex size-11 shrink-0 items-center justify-center rounded-md border border-white/30 transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:hidden"
              aria-label={t('shell.openMenu')}
              aria-controls="mobile-navigation"
              aria-expanded={drawerOpen}
            >
              <Icon name="menu" size={23} />
            </button>

            <div className="flex min-w-0 items-center gap-2.5">
              <Link
                to="/dashboard"
                className="flex shrink-0 items-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <BrandLogo className="h-8 sm:h-9" />
              </Link>
              <span className="hidden rounded-full bg-white px-2 py-1 text-xs font-bold text-brand lg:inline-flex">
                {customerId}
              </span>
            </div>

            <div className="ml-auto flex items-center gap-1 sm:gap-3">
              <Link
                to="/wallet/balance"
                data-tour="header-wallet"
                aria-label={
                  profile ? `${t('shell.walletBalance')}: ${balanceText}` : t('shell.walletBalance')
                }
                className="flex min-h-11 items-center gap-1.5 rounded-full border border-white/55 px-2.5 transition-colors hover:border-white hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white sm:gap-2 sm:px-3 lg:min-h-9"
              >
                <span className="hidden text-[10px] leading-none font-semibold tracking-[0.07em] text-white/80 uppercase sm:inline">
                  {t('shell.walletBalance')}
                </span>
                <span className="text-xs leading-none font-bold tabular-nums sm:text-sm">
                  {balanceText}
                </span>
              </Link>

              <Link
                to="/account/notifications"
                data-tour="header-notifications"
                className="relative flex size-11 items-center justify-center rounded-md transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white lg:size-9"
                aria-label={`${t('shell.notifications')}: ${fmt.number(unreadCount)}`}
              >
                <Icon name="notifications" size={22} />
                {unreadCount > 0 ? (
                  <span className="absolute top-0.5 right-0.5 flex size-5 items-center justify-center rounded-full border-2 border-brand bg-white text-[10px] font-bold text-brand md:-top-1 md:-right-1">
                    {fmt.unreadBadge(unreadCount)}
                  </span>
                ) : null}
              </Link>

              <AccountMenuButton />

              <LanguageSwitcher className="hidden lg:block" />
            </div>
          </div>
        </header>

        <div className="flex min-h-[calc(100dvh-4rem)]">
          <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-64 shrink-0 flex-col overflow-y-auto border-r border-line bg-surface lg:flex">
            <NavigationLinks />
            <div className="mt-auto border-t border-line p-3">
              <AppTourButton />
            </div>
          </aside>

          <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
            <div className="mx-auto w-full max-w-6xl">
              <SessionGate>
                {/*
                  MYP-136: the account pages share a "My account" menu — beside the page on a
                  laptop, as tabs above it on a phone.
                */}
                {isAccountPage(pathname) ? (
                  <div className="lg:flex lg:items-start lg:gap-8">
                    <div className="lg:sticky lg:top-24 lg:w-56 lg:shrink-0">
                      <AccountSectionNav />
                    </div>
                    <div className="min-w-0 lg:flex-1">{children}</div>
                  </div>
                ) : (
                  children
                )}
              </SessionGate>
            </div>
          </main>
        </div>

        {drawerOpen ? (
          <div className="fixed inset-0 z-50 lg:hidden">
            <button
              type="button"
              className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]"
              onClick={closeDrawer}
              aria-label={t('shell.closeMenu')}
            />
            <aside
              ref={drawerRef}
              id="mobile-navigation"
              role="dialog"
              aria-modal="true"
              aria-label={tNav('menu')}
              className="relative flex h-full w-[min(86vw,22rem)] flex-col overflow-y-auto rounded-r-xl bg-surface shadow-raised"
            >
              <div className="flex min-h-16 items-center gap-2 bg-brand px-4 text-white">
                <BrandLogo className="h-8" />
                <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-brand">
                  {customerId}
                </span>
                <button
                  type="button"
                  data-drawer-close
                  onClick={closeDrawer}
                  className="ml-auto flex size-11 items-center justify-center rounded-md transition-colors hover:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  aria-label={t('shell.closeMenu')}
                >
                  <Icon name="close" size={23} />
                </button>
              </div>

              <NavigationLinks onNavigate={closeDrawer} />

              <div className="mt-auto grid gap-5 border-t border-line p-4">
                <AppTourButton before={closeDrawer} />
                <LanguageSwitcher variant="segmented" />
              </div>
            </aside>
          </div>
        ) : null}
      </div>
    </TourProvider>
  );
}
